import React from "react";
import { useParams } from "react-router-dom";

import Box from "@mui/material/Box";

import { VehicleStore, FilterStore, CatalogStore } from "stores";

import { groupVehicles } from "../helper";

import Header from "./Header";
import ModelleCard from "../Modelle/Card";
import { EmptySearch } from "../";
import { TVehicle } from "types";

import { useSmartNavigate } from "helper/SmartLink";

const Modelle: React.FC = (): JSX.Element | null => {
  const { state: vehicles } = React.useContext(VehicleStore);
  const {
    state: { order: sortOrder },
    dispatch: dispatchFilter,
  } = React.useContext(FilterStore);

  const {
    state: { result: catalogIds },
  } = React.useContext(CatalogStore);

  const { modell_gruppe_id } = useParams();
  const navigate = useSmartNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (!(catalogIds?.modell_gruppe && modell_gruppe_id)) return;
    if (!catalogIds.modell_gruppe.includes(parseInt(modell_gruppe_id))) {
      navigate("/catalog", { replace: true });
      return;
    }
  }, [catalogIds, modell_gruppe_id, navigate]);

  if (!vehicles) return null;
  if (!modell_gruppe_id || !vehicles || vehicles.length === 0)
    return <EmptySearch />;

  const sorter = {
    empfohlen: (a: TVehicle, b: TVehicle) => {
      return b.preis > a.preis ? -1 : 1;
    },
    preis_asc: (a: TVehicle, b: TVehicle) => {
      return b.preis > a.preis ? -1 : 1;
    },
    preis_desc: (a: TVehicle, b: TVehicle) => {
      return b.preis < a.preis ? -1 : 1;
    },
    rate_asc: (a: TVehicle, b: TVehicle) => {
      if (!a.rate) return 1;
      if (!b.rate) return -1;
      return b.rate > a.rate ? -1 : 1;
    },
    rate_desc: (a: TVehicle, b: TVehicle) => {
      if (!a.rate) return 1;
      if (!b.rate) return -1;
      return b.rate < a.rate ? -1 : 1;
    },
    km_asc: (a: TVehicle, b: TVehicle) => {
      if (!a.km) return 1;
      if (!b.km) return -1;
      return b.km > a.km ? -1 : 1;
    },
    km_desc: (a: TVehicle, b: TVehicle) => {
      if (!a.km) return 1;
      if (!b.km) return -1;
      return b.km < a.km ? -1 : 1;
    },
    erstzulassung_asc: (a: TVehicle, b: TVehicle) => {
      if (!a.erstzulassung) return 1;
      if (!b.erstzulassung) return -1;
      return b.erstzulassung > a.erstzulassung ? -1 : 1;
    },
    erstzulassung_desc: (a: TVehicle, b: TVehicle) => {
      if (!a.erstzulassung) return 1;
      if (!b.erstzulassung) return -1;
      return b.erstzulassung < a.erstzulassung ? -1 : 1;
    },
  };

  const groups = groupVehicles(vehicles).sort(sorter[sortOrder]);

  const setModell = (id: any) => {
    const payload = {
      type: "UPDATE_FILTER",
      payload: { modell: [id] },
    };
    dispatchFilter(payload);
    navigate("/catalog/mo/" + id);
  };

  const detail = (id: any) => {
    navigate("/catalog/dt/" + id);
  };

  return (
    <>
      <Header id={modell_gruppe_id} />
      <Box
        sx={{
          display: "grid",
          columnGap: { xs: 1, sm: 2, md: 3 },
          rowGap: { xs: 2, md: 3, lg: 4 },
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
        }}
      >
        {groups.map(([modellId, group]: [string, any]) => {
          const handler = group.vorrat === 1 ? detail : setModell;
          const id = group.vorrat === 1 ? group.id : group.modell.id;
          return (
            <ModelleCard
              key={id}
              data={group}
              onClickHandler={() => handler(id)}
              paperProps={{
                sx: {
                  borderRadius: 1,
                  backgroundColor: "grey.300",
                  ":hover": {
                    borderColor: "grey.800",
                    backgroundColor: "grey.300",
                    cursor: "pointer",
                  },
                },
              }}
            />
          );
        })}
      </Box>
    </>
  );
};

export default Modelle;
